<template>
  <div class="app-map">
    <!-- vue3-google-map has no `options` prop: every google.maps.MapOptions entry is an individual
         prop, so spread them (vue2-google-maps took the whole object). The `app-map-inner` class
         lands on the lib's otherwise unstyled root wrapper via attribute fallthrough, giving
         _appMap.scss an explicit hook for its height. -->
    <GmapMap
      ref="mapRef"
      class="app-map-inner"
      :api-key="googleMapsKey"
      v-bind="mapOptions"
      :center="defaultCenter"
      :zoom="zoomLevel"
      map-type-id="roadmap"
      @click="onMapClick($event)"
      @rightclick="handleRightClick"
      @zoom_changed="onZoomChanged"
      @dragend="$emit('dragend', $event)"
    >
      <slot :map="map" />

      <template v-if="geolocationControl">
        <AppMapGeolocationControl @click="handleGeolocation" />
        <slot name="customMapControl" />
      </template>
    </GmapMap>
  </div>
</template>

<script>
import isMobile from '~/mixins/isMobile';
import { APPROX_EARTH_RADIUS } from '~/app_constants';

export default {
  name: 'AppMap',
  mixins: [isMobile],
  inject: {
    googleMapsKey: {
      from: 'googleMapsKey',
      default: '',
    },
  },
  props: {
    disablePoiInfoWindows: {
      type: Boolean,
      default: false,
    },
    mapCenterPosition: Object,
    fitMarkerWithRadius: Object,
    zoomLevel: Number,
    centerOnMarkerAndFitSubset: Object,
    enableContextMenu: {
      type: Boolean,
      default: false,
    },
    viewport: {
      type: Object,
      default() {
        return null;
      },
    },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    fitWithin: {
      type: Array,
      default() {
        return [];
      },
    },
    geolocationControl: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    'zoom-changed',
    'dragend',
    'viewport-adjustment-to-fit-radius',
    'view-port-adjustment',
    'detailed-zoom-threshold',
    'bounds-changed',
    'click',
    'open-menu',
    'current-marker',
    'location-changed',
  ],
  data() {
    return {
      map: null,
      mapCallbacks: [],
      defaultCenter: {
        lat: 0,
        lng: 0,
      },
      defaultMapOptions: {
        disableDefaultUI: true,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: 1,
          position: 11, // google.maps.ControlPosition.BOTTOM_CENTER
          mapTypeIds: ['roadmap', 'satellite'],
        },
        minZoom: 2,
        maxZoom: 19,
        zoomControl: true,
        zoomControlOptions: {
          style: 2,
          position: 6,
        },
        styles: [
          {
            featureType: 'all',
            elementType: 'labels',
            stylers: [
              {
                visibility: 'on',
              },
            ],
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'geometry.stroke',
            stylers: [
              {
                color: '#ffdb62',
              },
            ],
          },
          {
            featureType: 'landscape.natural',
            elementType: 'geometry',
            stylers: [
              {
                visibility: 'simplified',
              },
            ],
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry.fill',
            stylers: [
              {
                color: '#d4f1c9',
              },
            ],
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry.fill',
            stylers: [
              {
                color: '#fff8d1',
              },
            ],
          },
          {
            featureType: 'road.highway',
            elementType: 'labels',
            stylers: [
              {
                visibility: 'on',
              },
            ],
          },
          {
            featureType: 'road.arterial',
            elementType: 'labels.text',
            stylers: [
              {
                visibility: 'on',
              },
            ],
          },
          {
            featureType: 'road.local',
            elementType: 'labels.text',
            stylers: [
              {
                visibility: 'on',
              },
            ],
          },
          {
            featureType: 'water',
            elementType: 'geometry.fill',
            stylers: [
              {
                color: '#93dbff',
              },
            ],
          },
        ],
      },
    };
  },
  computed: {
    mapOptions() {
      // vue3-google-map spells the prop `disableDefaultUi` (lowercase "i"), while
      // google.maps.MapOptions — and our option objects — use `disableDefaultUI`.
      const { disableDefaultUI, ...options } = Object.assign({}, this.defaultMapOptions, this.options);

      if (disableDefaultUI !== undefined) {
        options.disableDefaultUi = disableDefaultUI;
      }

      return options;
    },
  },
  watch: {
    viewport(newValue, oldValue) {
      if (newValue === null) {
        return;
      }

      this.withMap((map) => {
        this.$nextTick(() => map.fitBounds(newValue));
      });
    },
    // watching changes to `mapCenterPosition` instead of binding it to the GmapMap component's property...
    // ... because the GmapMap component doesn't recenter the map if the new value is the same as the last non-null value
    mapCenterPosition(newValue, oldValue) {
      if (newValue == null) {
        return;
      }

      this.withMap((map) => {
        this.$nextTick(() => {
          // This will center as well, but the difference is that
          // if the distance is less than the map size, then it
          // animates to the new center. Otherwise it acts as
          // `map.setCenter()`
          map.panTo(newValue);
        });
      });
    },
    fitWithin(newValue, oldValue) {
      // At this point, the child GmapMap has been mounted, but
      // its map has not been initialized.
      // Therefore we need to write this.newValue(() => ...)

      if (newValue.length !== oldValue.length) {
        this.withMap((map) => {
          this.panMapToBounds(map, newValue);
        });
      }
    },
    fitMarkerWithRadius(newMarker, oldMarker) {
      if (newMarker == null) {
        return;
      }

      this.$emit('viewport-adjustment-to-fit-radius', 'in-progress');

      this.withMap((map) => {
        this.$nextTick(() => {
          const newCenter = {
            lat: parseFloat(newMarker.latitude || newMarker.position.lat),
            lng: parseFloat(newMarker.longitude || newMarker.position.lng),
          };
          // calculating the north-most and south-most points of the marker's radius; formula taken from https://stackoverflow.com/a/7478827
          const bounds = new window.google.maps.LatLngBounds();
          const northMostPointInRadiusPlusPadding = {
            lat: newCenter.lat + ((1.8 * newMarker.radius) / APPROX_EARTH_RADIUS) * (180 / Math.PI),
            lng: newCenter.lng,
          };

          bounds.extend(northMostPointInRadiusPlusPadding);

          const southMostPointInRadiusPlusPadding = {
            lat: newCenter.lat - ((1.8 * newMarker.radius) / APPROX_EARTH_RADIUS) * (180 / Math.PI),
            lng: newCenter.lng,
          };

          bounds.extend(southMostPointInRadiusPlusPadding);

          map.panToBounds(bounds);
          map.fitBounds(bounds);

          map.setCenter(newCenter);

          // `map.getDiv()` returns the map's own container element (Google Maps JS API) and works
          // regardless of the map library. The old `this.$el.querySelector('.vue-map-container')`
          // looked for a vue2-google-maps class that vue3-google-map doesn't render, so it returned
          // null → "Cannot read properties of null (reading 'offsetHeight')". Sibling components
          // (AppMapMarker, ActivitiesLocation*) already use `map.getDiv()`.
          const mapContainerDiv = map.getDiv();
          const mapDim = { height: mapContainerDiv.offsetHeight, width: mapContainerDiv.offsetWidth };
          const zoomLevel = this.getBoundsZoomLevel(bounds, mapDim);

          map.setZoom(zoomLevel);

          // we delay emitting this event so that the map was time to render and the viewport gets adjusted
          setTimeout(() => {
            this.$nextTick(() => {
              this.$emit('viewport-adjustment-to-fit-radius', 'ready');
            });
          }, 500);
        });
      });
    },
    centerOnMarkerAndFitSubset(newValue, oldValue) {
      if (!newValue) {
        return;
      }

      this.$emit('view-port-adjustment', 'in-progress');

      this.withMap((map) => {
        // As we receive the `map` object asynchronously we have to wait for the next Vue tick so that the map's behavior doesn't go nuts.
        // Not doing it like this have funky undesireable side-effects, ie: the move markers get drawn but don't get removed afterwards
        this.$nextTick(() => {
          const markers = newValue.markersSubset;
          const mainMarker = newValue.mainMarker;
          // set the viewport on the main marker and then include other markers if there are any
          const bounds = new window.google.maps.LatLngBounds();

          bounds.extend(mainMarker.position);
          if (markers && markers.length > 1) {
            markers.forEach((marker) => {
              bounds.extend(marker.position);
            });
          }
          map.panToBounds(bounds);
          map.fitBounds(bounds);

          // set the center on the main marker and then check if the others markers are still in view
          map.setCenter({
            lat: parseFloat(mainMarker.latitude || mainMarker.position.lat),
            lng: parseFloat(mainMarker.longitude || mainMarker.position.lng),
          });
          const centeredBounds = map.getBounds();
          const needsZoomOut =
            centeredBounds &&
            !!markers.find((marker) => {
              return !centeredBounds.contains(marker.position);
            });
          // if not all markers are in view then zooming out 1 level is enough to get them to show up in the viewport
          const detailedZoomThreshold = needsZoomOut ? map.getZoom() - 1 : map.getZoom();

          this.$emit('detailed-zoom-threshold', detailedZoomThreshold);

          if (needsZoomOut) {
            map.setZoom(detailedZoomThreshold);
          }

          this.$emit('view-port-adjustment', 'ready');
        });
      });
    },
  },
  beforeMount() {
    if (this.isMobile) {
      // Positon the satellite-map buttons on the bottom center so that they are not hidden behind the search bar
      //   google.maps.ControlPosition.BOTTOM_CENTER == 11
      this.defaultMapOptions.mapTypeControlOptions.position = 11;
    }
  },
  mounted() {
    const unwatch = this.$watch(
      () => this.$refs.mapRef?.ready,
      (ready) => {
        if (!ready) { return; }

        unwatch();

        const map = this.$refs.mapRef.map;

        this.map = map;
        // vue3-google-map only forwards its DECLARED props into the Map constructor, and some
        // google.maps.MapOptions entries (e.g. zoomControlOptions) aren't declared props — they
        // fall through as DOM attributes and never reach the map. Reapply the raw options object
        // once the map exists: google.maps accepts the full MapOptions shape here.
        map.setOptions(Object.assign({}, this.defaultMapOptions, this.options));
        this.mapCallbacks.forEach((cb) => cb(map));
        window.google.maps.event.addListenerOnce(map, 'projection_changed', () => {
          this.map = map;
        });

        if (this.fitWithin.length > 0) {
          this.panMapToBounds(map, this.fitWithin);

          window.google.maps.event.addListener(map, 'bounds_changed', () => {
            this.$nextTick(() => {
              this.$emit('bounds-changed', map.getBounds());
            });
          });
        }
      },
      { immediate: true },
    );
  },
  methods: {
    withMap(cb) {
      if (this.map) {
        cb(this.map);
      } else {
        this.mapCallbacks.push(cb);
      }
    },
    // vue3-google-map forwards the native `zoom_changed` event, which carries no payload
    // (vue2-google-maps used to pass the new zoom), so read the level from the map itself.
    onZoomChanged() {
      if (this.map) {
        this.$emit('zoom-changed', this.map.getZoom());
      }
    },
    onMapClick(event) {
      if (event.placeId && this.disablePoiInfoWindows) {
        event.stop();
      }

      this.$emit('click', event);
    },
    handleRightClick(e) {
      if (!this.enableContextMenu) {
        return;
      }

      this.$emit('open-menu', e);
    },
    handleGeolocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const newPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          const zoomLevel = 15;

          this.$emit('current-marker', newPosition);
          this.$emit('location-changed', newPosition, zoomLevel);
        });
      } else {
        this.$emit('current-marker', null);
      }
    },
    panMapToBounds(map, markers) {
      if (markers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();

        markers.forEach((marker) => {
          bounds.extend(marker.position);
        });
        map.panToBounds(bounds);
        map.fitBounds(bounds);
      }
    },
    // extracted from https://stackoverflow.com/a/13274361
    getBoundsZoomLevel(bounds, mapDim) {
      const WORLD_DIM = { height: 256, width: 256 };
      const ZOOM_MAX = 21;

      function latRad(lat) {
        const sin = Math.sin((lat * Math.PI) / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;

        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
      }

      function zoom(mapPx, worldPx, fraction) {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
      }

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;
      const lngDiff = ne.lng() - sw.lng();
      const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;
      const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
      const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

      return Math.min(latZoom, lngZoom, ZOOM_MAX);
    },
  },
};
</script>

<!-- NB: AppMap's sizing CSS lives in app/assets/stylesheets/components/_appMap.scss (always
     loaded via sprockets), NOT in a <style scoped> block here: scoped component CSS is emitted
     into a vite chunk's stylesheet, and production chunking is not deterministic — on the review
     build the AppMap chunk ended up dynamically imported and its CSS was never linked, collapsing
     the map to 0px height. -->
